import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./tooltips.css";

const TRIGGERS = 'button, [role="button"], input[type="number"], input[type="range"], output, summary, [data-help], label[title]';
const DELAY = 1000;
const GAP = 8;

type Tip = { target: HTMLElement; text: string; visible: boolean };

function description(target: HTMLElement) {
  const explicit = target.getAttribute("data-help");
  if (explicit) return explicit;
  const title = target.getAttribute("title");
  if (title) return title;
  const name = target.getAttribute("aria-label") || target.textContent?.replace(/\s+/g, " ").trim();
  const details = target.getAttribute("aria-description");
  return [name, details].filter(Boolean).join(". ");
}

/** One delegated tooltip layer, including lazy panels, disabled controls and full screen. */
export function TooltipProvider() {
  const id = `control-help-${useId().replace(/:/g, "")}`;
  const node = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const [host, setHost] = useState<Element>(() => document.fullscreenElement ?? document.body);

  useEffect(() => {
    let current: Tip | null = null;
    let openTimer: ReturnType<typeof setTimeout> | undefined;
    let closeTimer: ReturnType<typeof setTimeout> | undefined;
    let validityTimer: ReturnType<typeof setTimeout> | undefined;
    let pointerFocus = false;
    let suppressed: HTMLElement | null = null;
    // Suppress the browser's duplicate title while a pointer remains on a control.
    let nativeTitles: { element: HTMLElement; value: string | null }[] = [];
    const restoreTitles = () => {
      for (const { element, value } of nativeTitles) {
        if (element.getAttribute("title") !== "") continue;
        if (value === null) element.removeAttribute("title");
        else element.setAttribute("title", value);
      }
      nativeTitles = [];
    };
    const suppressTitles = (target: HTMLElement) => {
      restoreTitles();
      for (let element: HTMLElement | null = target; element; element = element.parentElement) {
        if (element === target || element.hasAttribute("title")) {
          nativeTitles.push({ element, value: element.getAttribute("title") });
          element.setAttribute("title", "");
        }
      }
    };
    const clear = () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      clearTimeout(validityTimer);
      if (current) {
        if (current.visible) current.target.dispatchEvent(new CustomEvent("cue-help-visible", { bubbles: true, detail: { visible: false } }));
        const ids = (current.target.getAttribute("aria-describedby") ?? "").split(/\s+/).filter((value) => value && value !== id);
        if (ids.length) current.target.setAttribute("aria-describedby", ids.join(" "));
        else current.target.removeAttribute("aria-describedby");
      }
      current = null;
      setTip(null);
    };
    const dismiss = () => {
      suppressed = current?.target ?? suppressed;
      clear();
    };
    const trigger = (target: EventTarget | null) => target instanceof Element ? target.closest<HTMLElement>(TRIGGERS) : null;
    const start = (target: HTMLElement | null, pointer = false) => {
      if (!target || target === suppressed || node.current?.contains(target)) return;
      clearTimeout(closeTimer);
      if (current?.target === target) {
        if (pointer && !nativeTitles.length) suppressTitles(target);
        return;
      }
      const text = description(target);
      clear();
      if (!text || target.closest('[inert], [aria-hidden="true"]')) return;
      current = { target, text, visible: false };
      const ids = new Set((target.getAttribute("aria-describedby") ?? "").split(/\s+/).filter(Boolean));
      ids.add(id);
      target.setAttribute("aria-describedby", [...ids].join(" "));
      if (pointer) suppressTitles(target);
      setTip(current);
      openTimer = setTimeout(() => {
        if (!current || !target.isConnected || !target.getClientRects().length) { clear(); return; }
        current = { ...current, text: target.getAttribute("data-help") || text, visible: true };
        target.dispatchEvent(new CustomEvent("cue-help-visible", { bubbles: true, detail: { visible: true } }));
        setTip(current);
        const checkAnchor = () => {
          if (!current) return;
          if (!target.isConnected || !target.getClientRects().length || document.hidden) { clear(); restoreTitles(); return; }
          const latest = target.getAttribute("data-help");
          if (latest && latest !== current.text) {
            current = { ...current, text: latest };
            setTip(current);
          }
          validityTimer = setTimeout(checkAnchor, 250);
        };
        validityTimer = setTimeout(checkAnchor, 250);
      }, DELAY);
    };
    const over = (event: PointerEvent) => {
      if (event.pointerType === "touch" || event.buttons) return;
      if (node.current?.contains(event.target as Node)) { clearTimeout(closeTimer); return; }
      const target = trigger(event.target);
      if (target !== suppressed) suppressed = null;
      start(target, true);
    };
    const out = (event: PointerEvent) => {
      const next = event.relatedTarget instanceof Node ? event.relatedTarget : null;
      if (current?.target.contains(next) || node.current?.contains(next)) return;
      if (suppressed && !suppressed.contains(next)) suppressed = null;
      const fromHelp = current?.target.contains(event.target as Node) || node.current?.contains(event.target as Node);
      // Pointer events elsewhere in the document do not own this help session.
      if (!fromHelp) {
        if (!current) restoreTitles();
        return;
      }
      restoreTitles();
      if (current?.target.contains(document.activeElement)) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(clear, 150);
    };
    const focus = (event: FocusEvent) => {
      if (pointerFocus) return;
      suppressed = null;
      start(trigger(event.target));
    };
    const blur = (event: FocusEvent) => {
      if (!current?.target.contains(event.target as Node)) return;
      if (current.target.contains(event.relatedTarget as Node | null)) return;
      if (current.target.matches(":hover") || node.current?.matches(":hover")) return;
      clear();
    };
    const press = () => { pointerFocus = true; dismiss(); };
    const key = (event: KeyboardEvent) => {
      pointerFocus = false;
      if (event.key === "Escape" && current?.visible) {
        // Dismiss help first; a second Escape can close its surrounding panel.
        event.preventDefault();
        event.stopPropagation();
      }
      dismiss();
    };
    const scroll = (event: Event) => {
      const scroller = event.target;
      if (scroller === document || (scroller instanceof Element && scroller.contains(current?.target ?? null))) dismiss();
    };
    const fullscreen = () => {
      clear();
      restoreTitles();
      suppressed = null;
      setHost(document.fullscreenElement ?? document.body);
    };
    const leaveWindow = () => { dismiss(); restoreTitles(); };
    document.addEventListener("pointerover", over, true);
    document.addEventListener("pointerout", out, true);
    document.addEventListener("pointerdown", press, true);
    document.addEventListener("focusin", focus, true);
    document.addEventListener("focusout", blur, true);
    document.addEventListener("keydown", key, true);
    document.addEventListener("input", dismiss, true);
    document.addEventListener("change", dismiss, true);
    document.addEventListener("scroll", scroll, true);
    document.addEventListener("fullscreenchange", fullscreen);
    window.addEventListener("resize", dismiss);
    window.addEventListener("blur", leaveWindow);
    return () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      clear();
      restoreTitles();
      document.removeEventListener("pointerover", over, true);
      document.removeEventListener("pointerout", out, true);
      document.removeEventListener("pointerdown", press, true);
      document.removeEventListener("focusin", focus, true);
      document.removeEventListener("focusout", blur, true);
      document.removeEventListener("keydown", key, true);
      document.removeEventListener("input", dismiss, true);
      document.removeEventListener("change", dismiss, true);
      document.removeEventListener("scroll", scroll, true);
      document.removeEventListener("fullscreenchange", fullscreen);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("blur", leaveWindow);
    };
  }, [id]);

  useLayoutEffect(() => {
    const element = node.current;
    if (!element) return;
    if (!tip?.visible) {
      if (element.matches(":popover-open")) element.hidePopover();
      return;
    }
    if (!element.matches(":popover-open")) element.showPopover();
    const position = () => {
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft ?? 0;
      const top = viewport?.offsetTop ?? 0;
      const width = viewport?.width ?? window.innerWidth;
      const height = viewport?.height ?? window.innerHeight;
      const anchor = tip.target.getBoundingClientRect();
      const rightSpace = left + width - anchor.right - GAP * 2;
      // Vertical navigation help belongs beside its rail, so a hover never
      // covers the next tab. Wrap the help to fit even beside a phone-sized rail.
      const beside = tip.target.dataset.helpPlacement === "right" && rightSpace >= 120;
      element.style.maxWidth = `${Math.min(320, beside ? rightSpace : width - GAP * 2)}px`;
      const box = element.getBoundingClientRect();
      const below = anchor.bottom + GAP;
      const y = beside ? anchor.top + (anchor.height - box.height) / 2
        : below + box.height <= top + height - GAP ? below : anchor.top - box.height - GAP;
      const x = beside ? anchor.right + GAP : anchor.left + (anchor.width - box.width) / 2;
      element.style.left = `${Math.max(left + GAP, Math.min(x, left + width - box.width - GAP))}px`;
      element.style.top = `${Math.max(top + GAP, Math.min(y, top + height - box.height - GAP))}px`;
    };
    position();
    const observer = new ResizeObserver(position);
    observer.observe(element);
    return () => observer.disconnect();
  }, [tip, host]);

  // Modal dialogs make siblings inert, including body-hosted help. Keep a
  // dialog control's tooltip inside that dialog while retaining the top layer.
  const helpHost = tip?.target.closest("dialog[open]") ?? host;
  return tip ? createPortal(<div id={id} ref={node} role="tooltip" popover="manual" className="control-tooltip">{tip.text}</div>, helpHost) : null;
}
